import React, { useState, useEffect, useRef } from 'react';
import { 
  SupportTicket, 
  SupportTicketCategory, 
  SupportTicketPriority,
  CreateSupportTicketResponse,
  GetSupportTicketsResponse 
} from '../types';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Dropdown, { DropdownOption } from '../components/ui/Dropdown';
import Toast from '../components/ui/Toast';
import { 
  LifeBuoy, 
  Plus, 
  Paperclip, 
  X, 
  Bug, 
  Lightbulb, 
  BookOpen, 
  CreditCard, 
  Wrench, 
  HelpCircle,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Send
} from 'lucide-react';
import { api } from '../services/api';
import { useSubscriptionError } from '../context/SubscriptionErrorContext';
import { handleApiError } from '../utils/errorHandling';
import { useAuth } from '../hooks/useAuth';

const categoryOptions: DropdownOption<SupportTicketCategory>[] = [
  { value: 'bug', label: 'Bug Report', icon: <Bug className="w-4 h-4 text-red-500" /> },
  { value: 'feature_request', label: 'Feature Request', icon: <Lightbulb className="w-4 h-4 text-yellow-500" /> },
  { value: 'knowledge', label: 'Knowledge / Documentation', icon: <BookOpen className="w-4 h-4 text-blue-500" /> },
  { value: 'billing', label: 'Billing', icon: <CreditCard className="w-4 h-4 text-green-500" /> },
  { value: 'technical_support', label: 'Technical Support', icon: <Wrench className="w-4 h-4 text-purple-500" /> },
  { value: 'general_inquiry', label: 'General Inquiry', icon: <HelpCircle className="w-4 h-4 text-gray-500" /> },
];

const priorityOptions: DropdownOption<SupportTicketPriority>[] = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300' },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' },
];

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'open':
      return <AlertCircle className="w-4 h-4 text-blue-500" />;
    case 'in_progress':
      return <Clock className="w-4 h-4 text-yellow-500" />;
    case 'resolved':
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'closed':
      return <XCircle className="w-4 h-4 text-gray-500" />;
    default:
      return <AlertCircle className="w-4 h-4 text-gray-500" />;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'open':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
    case 'in_progress':
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300';
    case 'resolved':
      return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
    case 'closed':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  }
};

const getCategoryIcon = (category: SupportTicketCategory) => {
  const option = categoryOptions.find(opt => opt.value === category);
  return option?.icon || <HelpCircle className="w-4 h-4 text-gray-500" />;
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const SupportPage: React.FC = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [closingTicketId, setClosingTicketId] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingTicketId, setEditingTicketId] = useState<string | null>(null);
  const [editSubject, setEditSubject] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPriority, setEditPriority] = useState<SupportTicketPriority>('medium');
  const [savingEdit, setSavingEdit] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [expandedTicketId, setExpandedTicketId] = useState<string | null>(null);
  
  // Form state
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<SupportTicketCategory>('general_inquiry');
  const [priority, setPriority] = useState<SupportTicketPriority>('medium');
  
  const { setSubscriptionError } = useSubscriptionError();
  const { user } = useAuth();

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<GetSupportTicketsResponse>('/api/support/tickets');
      setTickets(response.tickets || []);
      setSubscriptionError(null);
    } catch (err: any) {
      handleApiError(err, setError, setSubscriptionError);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const resetForm = () => {
    setSubject('');
    setDescription('');
    setCategory('general_inquiry');
    setPriority('medium');
    setAttachments([]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) setAttachments([...attachments, ...Array.from(files)].slice(0, 5));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const startEditing = (ticket: SupportTicket) => {
    setEditingTicketId(ticket.ticketId);
    setEditSubject(ticket.subject);
    setEditDescription(ticket.description);
    setEditPriority(ticket.priority);
  };

  const handleEdit = async (ticketId: string) => {
    if (!editSubject.trim() || !editDescription.trim()) {
      setError('A ticket needs a subject and a description.');
      return;
    }

    setSavingEdit(true);
    setError(null);
    try {
      await api.patch(`/api/support/tickets/${ticketId}`, {
        subject: editSubject.trim(),
        description: editDescription.trim(),
        priority: editPriority,
      });
      setEditingTicketId(null);
      await fetchTickets();
    } catch (err: any) {
      handleApiError(err, setError, setSubscriptionError);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleClose = async (ticketId: string) => {
    setClosingTicketId(ticketId);
    setError(null);
    try {
      await api.post(`/api/support/tickets/${ticketId}/close`, {});
      await fetchTickets();
    } catch (err: any) {
      handleApiError(err, setError, setSubscriptionError);
    } finally {
      setClosingTicketId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!subject.trim() || !description.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Multipart only when there is something to carry. A form with no files has no business
      // being a file upload, and JSON is what the endpoint documents first.
      const response = attachments.length
        ? await (() => {
            const form = new FormData();
            form.append('subject', subject.trim());
            form.append('description', description.trim());
            form.append('category', category);
            form.append('priority', priority);
            attachments.forEach(file => form.append('attachments', file));
            return api.upload<CreateSupportTicketResponse>('/api/support/tickets', form);
          })()
        : await api.post<CreateSupportTicketResponse>('/api/support/tickets', {
            subject: subject.trim(),
            description: description.trim(),
            category,
            priority,
          });

      setSuccessMessage(
        response?.message ||
          'Your support ticket has been submitted. We will get back to you by email.'
      );
      setShowCreateForm(false);
      resetForm();
      await fetchTickets();
    } catch (err: any) {
      handleApiError(err, setError, setSubscriptionError);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleTicketExpand = (ticketId: string) => {
    setExpandedTicketId(prev => prev === ticketId ? null : ticketId);
  };

  return (
    <div className="p-4 lg:p-8 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <LifeBuoy className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold dark:text-white text-gray-900">Support</h1>
            <p className="text-sm dark:text-gray-400 text-gray-600">
              Submit and track your support tickets
            </p>
          </div>
        </div>
        
        {!showCreateForm && (
          <Button onClick={() => setShowCreateForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Ticket
          </Button>
        )}
      </div>

      {/* Toast Messages */}
      {error && (
        <Toast 
          message={error} 
          type="error" 
          onClose={() => setError(null)} 
        />
      )}
      {successMessage && (
        <Toast 
          message={successMessage} 
          type="success" 
          onClose={() => setSuccessMessage(null)} 
        />
      )}

      {/* Create Ticket Form */}
      {showCreateForm && (
        <Card title="Create Support Ticket" className="animate-fadeIn">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Subject */}
            <Input
              id="subject"
              label="Subject *"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief summary of your issue"
              required
            />

            {/* Category and Priority */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Dropdown
                  label="Category *"
                  options={categoryOptions}
                  value={category}
                  onChange={setCategory}
                  placeholder="Select category"
                />
              </div>
              <div>
                <Dropdown
                  label="Priority"
                  options={priorityOptions}
                  value={priority}
                  onChange={setPriority}
                  placeholder="Select priority"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label 
                htmlFor="description" 
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Description *
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Please describe your issue in detail. Include any error messages, steps to reproduce, or relevant information that can help us assist you better."
                rows={6}
                required
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 
                          bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                          placeholder-gray-500 dark:placeholder-gray-400
                          shadow-sm focus:border-primary focus:ring-primary 
                          sm:text-sm py-2 px-3 resize-none"
              />
            </div>

            {/* File Attachments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Attachments <span className="font-normal text-gray-500">— up to 5, 10MB each</span>
              </label>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                accept=".jpg,.jpeg,.png,.gif,.pdf,.txt,.log,.csv,.json,.zip"
                className="hidden"
              />

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={attachments.length >= 5}
                className="flex items-center gap-2 px-4 py-2 border-2 border-dashed
                          border-gray-300 dark:border-gray-600 rounded-lg
                          hover:border-primary dark:hover:border-primary
                          transition-colors cursor-pointer
                          disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Paperclip className="w-5 h-5 dark:text-gray-400 text-gray-500" />
                <span className="dark:text-gray-400 text-gray-600">
                  {attachments.length >= 5 ? 'Five is the limit' : 'Attach a screenshot or a log'}
                </span>
              </button>

              {attachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {attachments.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg
                                dark:bg-gray-800 bg-gray-100"
                    >
                      <span className="truncate text-sm dark:text-gray-300 text-gray-700">
                        {file.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="text-xs text-red-500 hover:text-red-600 flex-shrink-0"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700 border-gray-200">
              <Button 
                type="button" 
                variant="secondary" 
                onClick={() => {
                  setShowCreateForm(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                isLoading={submitting}
                disabled={!subject.trim() || !description.trim()}
              >
                <Send className="w-4 h-4 mr-2" />
                Submit Ticket
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Tickets List */}
      <Card title="Your Tickets">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-12">
            <LifeBuoy className="w-12 h-12 mx-auto dark:text-gray-500 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium dark:text-gray-300 text-gray-700 mb-2">
              No tickets yet
            </h3>
            <p className="text-sm dark:text-gray-500 text-gray-500 mb-4">
              When you need help, create a support ticket and we'll assist you.
            </p>
            {!showCreateForm && (
              <Button onClick={() => setShowCreateForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create your first ticket
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <div 
                key={ticket._id || ticket.ticketId}
                className="border dark:border-gray-700 border-gray-200 rounded-lg 
                          hover:border-primary/50 transition-colors"
              >
                {/* Ticket Header */}
                <button
                  onClick={() => toggleTicketExpand(ticket._id || ticket.ticketId)}
                  className="w-full p-4 flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {getCategoryIcon(ticket.category)}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium dark:text-white text-gray-900 truncate">
                          {ticket.subject}
                        </span>
                        <span className="text-xs dark:text-gray-500 text-gray-400">
                          #{ticket.ticketId}
                        </span>
                      </div>
                      <div className="text-sm dark:text-gray-400 text-gray-500">
                        {formatDate(ticket.createdAt)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                      {getStatusIcon(ticket.status)}
                      {ticket.status.replace('_', ' ')}
                    </span>
                    {/* The stage the team is actually at. "In progress" covers everything from
                        picked-up to awaiting release, and a merchant watching their own bug wants
                        to know which. */}
                    {ticket.trackerStatus && ticket.status !== 'closed' && (
                      <span className="hidden sm:inline text-xs dark:text-gray-400 text-gray-500">
                        {ticket.trackerStatus}
                      </span>
                    )}
                    {expandedTicketId === (ticket._id || ticket.ticketId) ? (
                      <ChevronUp className="w-5 h-5 dark:text-gray-400 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 dark:text-gray-400 text-gray-500" />
                    )}
                  </div>
                </button>

                {/* Expanded Content */}
                {expandedTicketId === (ticket._id || ticket.ticketId) && (
                  <div className="px-4 pb-4 border-t dark:border-gray-700 border-gray-200 pt-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <span className="text-xs dark:text-gray-500 text-gray-400 uppercase">Category</span>
                        <div className="flex items-center gap-2 mt-1">
                          {getCategoryIcon(ticket.category)}
                          <span className="text-sm dark:text-gray-300 text-gray-700 capitalize">
                            {ticket.category.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                      <div>
                        <span className="text-xs dark:text-gray-500 text-gray-400 uppercase">Priority</span>
                        <div className="mt-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            priorityOptions.find(p => p.value === ticket.priority)?.color || ''
                          }`}>
                            {ticket.priority}
                          </span>
                        </div>
                      </div>
                      <div>
                        <span className="text-xs dark:text-gray-500 text-gray-400 uppercase">Created</span>
                        <div className="text-sm dark:text-gray-300 text-gray-700 mt-1">
                          {formatDate(ticket.createdAt)}
                        </div>
                      </div>
                      <div>
                        <span className="text-xs dark:text-gray-500 text-gray-400 uppercase">Updated</span>
                        <div className="text-sm dark:text-gray-300 text-gray-700 mt-1">
                          {formatDate(ticket.updatedAt)}
                        </div>
                      </div>
                    </div>
                    
                    {editingTicketId === ticket.ticketId ? (
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs dark:text-gray-500 text-gray-400 uppercase">
                            Subject
                          </label>
                          <input
                            value={editSubject}
                            onChange={e => setEditSubject(e.target.value)}
                            maxLength={300}
                            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600
                                      dark:bg-gray-800 bg-white dark:text-gray-100 text-gray-900
                                      text-sm py-2 px-3"
                          />
                        </div>
                        <div>
                          <label className="text-xs dark:text-gray-500 text-gray-400 uppercase">
                            Description
                          </label>
                          <textarea
                            value={editDescription}
                            onChange={e => setEditDescription(e.target.value)}
                            rows={5}
                            maxLength={10000}
                            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600
                                      dark:bg-gray-800 bg-white dark:text-gray-100 text-gray-900
                                      text-sm py-2 px-3 resize-none"
                          />
                        </div>
                        <div>
                          <label className="text-xs dark:text-gray-500 text-gray-400 uppercase">
                            Priority
                          </label>
                          <select
                            value={editPriority}
                            onChange={e => setEditPriority(e.target.value as SupportTicketPriority)}
                            className="mt-1 block rounded-md border-gray-300 dark:border-gray-600
                                      dark:bg-gray-800 bg-white dark:text-gray-100 text-gray-900
                                      text-sm py-2 px-3"
                          >
                            {priorityOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" isLoading={savingEdit} onClick={() => handleEdit(ticket.ticketId)}>
                            Save changes
                          </Button>
                          <Button size="sm" variant="secondary" onClick={() => setEditingTicketId(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <span className="text-xs dark:text-gray-500 text-gray-400 uppercase">Description</span>
                        <p className="mt-1 text-sm dark:text-gray-300 text-gray-700 whitespace-pre-wrap">
                          {ticket.description}
                        </p>
                      </div>
                    )}

                    {ticket.status !== 'closed' && editingTicketId !== ticket.ticketId && (
                      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                        <p className="text-xs dark:text-gray-400 text-gray-500">
                          Something to add or correct, or no longer need this?
                        </p>
                        <div className="flex items-center gap-2">
                          <Button variant="secondary" size="sm" onClick={() => startEditing(ticket)}>
                            Edit
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            isLoading={closingTicketId === ticket.ticketId}
                            onClick={() => handleClose(ticket.ticketId)}
                          >
                            Close ticket
                          </Button>
                        </div>
                      </div>
                    )}

                    {ticket.attachments && ticket.attachments.length > 0 && (
                      <div className="mt-4">
                        <span className="text-xs dark:text-gray-500 text-gray-400 uppercase">Attachments</span>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {ticket.attachments.map((attachment) => (
                            <div 
                              key={attachment.id}
                              className="flex items-center gap-2 px-3 py-1.5 rounded-lg 
                                        bg-gray-50 dark:bg-gray-800 border 
                                        border-gray-200 dark:border-gray-700 text-sm"
                            >
                              <Paperclip className="w-3 h-3 dark:text-gray-400 text-gray-500" />
                              <span className="dark:text-gray-300 text-gray-700">
                                {attachment.fileName}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default SupportPage;
